import React, { FC } from 'react';
import CheckCircle from '@atlaskit/icon/glyph/check-circle';
import CrossCircle from '@atlaskit/icon/glyph/cross-circle';
import JiraFailedBuildStatusIcon from '@atlaskit/icon/glyph/jira/failed-build-status';
import Tooltip from '@atlaskit/tooltip';
import { css } from '@emotion/core';
import { SloDTO, SloGroupSummaryDTO, SloType } from '@atlassiansox/phobos-client-ts';
import { token } from '@atlaskit/tokens';
import { Link } from 'react-router-dom';
import ChildIssuesIcon from '@atlaskit/icon/glyph/child-issues';

import {
  textOverflowHidden,
  BREACH_COLOR,
  MEET_COLOR,
  colors,
  whiteSpaceBreakSpaces,
  marginPushRight,
  QUALITY_ISSUE_COLOR,
  spaceGapMinimum,
  flexCenter,
} from '@/styles';
import { SkeletonCopy, LinkWithRef } from '@/components/primitive';
import SfxIcon from '@/components/primitive/sfx-icon';
import SloMenu from '@/components/primitive/slo-menu';
import MimirLogo from '@/components/primitive/mimir/logo';

import DraftStatusLozenge from '../lozenges/draft-status-lozenge';
import SloTypeLozenge from '../lozenges/slo-type-lozenge';
import { determineSloStatus, SLINumberProps } from '../utils';
import { getSLOLifecycleStatus } from '../../new-slo-views/common/utils';
import { isIndicatorTypeMimir } from '../../slo-group/utils';

import * as styles from './styles';

interface SLOCardHeaderProps {
  publicId: string;
  name: string;
  type: SloType;
  indicator?: SloDTO['indicator'] & {
    /* NOTE: temporary until DTO is updated */
    uiMetrics?: { population?: { value?: { perfPortalUrl?: string } } };
  };
  shouldTruncate?: boolean;
  isDraft: boolean;
  isSnapshot?: boolean;
  isAlertable?: boolean;
  isSkeleton?: boolean;
  isCompact?: boolean;
  isGroup?: boolean;
  sloGroupSummary?: SloGroupSummaryDTO;
}

const QUALITY_ISSUE_TEXT = (
  <>
    One or more SLO quality issues were found during analysis.
    <br />
    <br />
    Please check details on SLO page.
  </>
);

const SLOCardHeader: FC<SLOCardHeaderProps & SLINumberProps> = ({
  publicId,
  name,
  type,
  performance,
  objective,
  indicator,
  shouldTruncate,
  isDraft,
  isAlertable,
  isSkeleton,
  isCompact,
  quality,
  isGroup,
  sloGroupSummary,
  isSnapshot,
}) => {
  const isMimir = isIndicatorTypeMimir(indicator);
  const groupStyles = isGroup ? styles.layoutGroup : null;
  const mimirStyles = isMimir ? styles.layoutMimir : null;
  return (
    // eslint-disable-next-line no-nested-ternary
    <div css={[styles.layout, groupStyles, mimirStyles]}>
      <div css={styles.content}>
        {(() => {
          switch (determineSloStatus({ performance, objective, isGroup, sloGroupSummary })) {
            case 'meets': {
              return (
                <Tooltip
                  content={
                    isGroup
                      ? 'All members of this SLO group meet their objectives.'
                      : `This SLO meets the Objective of ${objective}%.`
                  }
                >
                  <CheckCircle label="SLO Meets" primaryColor={MEET_COLOR} size="small" />
                </Tooltip>
              );
            }
            case 'breaches': {
              return (
                <Tooltip
                  content={
                    isGroup
                      ? 'Some members of this SLO group are breaching their objectives.'
                      : `This SLO is currently breaching ${objective}%.`
                  }
                >
                  <CrossCircle label="SLO Breaches" primaryColor={BREACH_COLOR} size="small" />
                </Tooltip>
              );
            }
            default: {
              return (
                <Tooltip
                  content={`Something might be wrong with this SLO${isGroup ? ' group' : ''}.`}
                >
                  <JiraFailedBuildStatusIcon
                    label="SLO Status unknown"
                    primaryColor={token('color.text.subtlest', colors.N100)}
                    size="small"
                  />
                </Tooltip>
              );
            }
          }
        })()}
        {quality?.hasQualityIssues && (
          <span css={styles.titleQualityIssueIconStyles}>
            <Tooltip content={QUALITY_ISSUE_TEXT}>
              <Link
                relative="path"
                to={`/${isGroup ? `slo-${isMimir ? 'mimir' : 'group'}` : 'slo'}/${publicId}`}
              >
                <JiraFailedBuildStatusIcon
                  size="small"
                  label="quality issue icon"
                  primaryColor={QUALITY_ISSUE_COLOR}
                />
              </Link>
            </Tooltip>
          </span>
        )}

        <Tooltip content={name}>
          <LinkWithRef
            href={`/${isGroup ? `slo-${isMimir ? 'mimir' : 'group'}` : 'slo'}/${publicId}`}
          >
            <h5
              css={[
                shouldTruncate ? textOverflowHidden : whiteSpaceBreakSpaces,
                css`
                  color: ${token('color.text.subtle', colors.N500)};
                  ${shouldTruncate && `max-width: 200px;`};
                `,
              ]}
            >
              {isSkeleton ? <SkeletonCopy width={150} /> : name}
            </h5>
          </LinkWithRef>
        </Tooltip>
        <span css={[marginPushRight, flexCenter, spaceGapMinimum]}>
          {isDraft && <DraftStatusLozenge isCompact={isCompact} />}
          <SloTypeLozenge type={type} isCompact={isCompact} isMimir={isMimir} />
        </span>
      </div>
      <div css={styles.actions}>
        {!isDraft && isAlertable && (
          <Tooltip
            content="This SLO will raise alerts via SignalFx.
            When this SLO experiences a breach or burn event,
            it will page the owning team via OpsGenie"
            position="right"
          >
            <SfxIcon />
          </Tooltip>
        )}
        {isGroup && (
          <Tooltip
            content="This is an SLO group SLO, click card to see SLO group details."
            position="right"
          >
            <ChildIssuesIcon size="small" label="" />
          </Tooltip>
        )}
        {isMimir && <MimirLogo />}
        <SloMenu
          sloLifecycleStatus={getSLOLifecycleStatus(isDraft, isSnapshot)}
          publicId={publicId}
          isSkeleton={isSkeleton}
          name={name}
          indicator={indicator}
          isGroupCard={isGroup}
        />
      </div>
    </div>
  );
};

export default SLOCardHeader;
